import { Box, Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';

import type { StaticImageData } from 'Components/Image';
import { TwoUp } from 'Components/TwoUp';

import { BackgroundVideo } from 'Blocks/BackgroundVideo';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Separator = styled.div`
  width: 98%;
  border-top: 1px solid ${theme.colors.primary[200]};
  opacity: 0.5;
  margin: 0 auto;
`;

type HowToUseProps = {
  label?: string;
  title: string;
  description: string;
  amount: string;
  steps: string[];
  video: string;
  fitCover?: boolean;
  posterImage: StaticImageData;
};

export const HowToUse = ({
  label,
  title,
  description,
  amount,
  steps,
  video,
  posterImage,
  fitCover = true,
}: HowToUseProps) => {
  const { isMobile } = useResponsiveVariant({ legacy: false });
  const bodyFontSize = isMobile ? 'body4regular' : 'body3regular';

  return (
    <TwoUp
      desktopOrder="21"
      spaceDistribution={{ desktop: 'equal', mobile: 'contents' }}
      threshold="md"
    >
      <BackgroundVideo
        autoplayDelay={1000}
        fitCover={fitCover}
        imageAlt="Model using Prose Custom Scalp Serum on their scalp"
        isLegacyBreakpoints={false}
        lazyLoad
        posterImages={posterImage}
        trackingText="Density Set How to Use Video"
        videoSources={video}
      />
      <Box
        alignItems="center"
        backgroundColor="primary.300"
        display="flex"
        flexDirection="column"
        justifyContent={{ mobile: 'flex-start', tablet: 'center', desktop: 'center' }}
        paddingX={{ mobile: '6x', tablet: '25x', desktop: '25x' }}
        paddingY={{ mobile: '15x', tablet: '25x', desktop: '25x' }}
        w="full"
      >
        <Box display="flex" flexDirection="column" gap="6x">
          {label && (
            <Text color="neutral.300" variant="label2regular">
              {label}
            </Text>
          )}
          <Text as="h2" color="neutral.300" variant="h3">
            {title}
          </Text>
          <Text as="p" color="neutral.100" variant={bodyFontSize}>
            {description}
          </Text>
          <Separator />
          <Box display="flex" flexDirection="column" gap="3x">
            <Text color="neutral.100" variant="label2regular">
              Amount
            </Text>
            <Text color="neutral.100" variant={bodyFontSize}>
              {amount}
            </Text>
          </Box>
          <Separator />
          <Text color="neutral.100" variant="label2regular">
            Directions
          </Text>
          {steps.map((step, i) => (
            <Box key={`step ${i + 1}`} alignItems="flex-start" display="flex" gap="4x">
              <Text
                __borderRadius="50%"
                __padding="0 7px"
                border="1px"
                borderColor="neutral.100"
                color="neutral.100"
                variant={bodyFontSize}
              >
                {i + 1}
              </Text>
              <Text color="neutral.100" variant={bodyFontSize}>
                {step}
              </Text>
            </Box>
          ))}
        </Box>
      </Box>
    </TwoUp>
  );
};
